import { isDataClientError, type ClubApplicationInput } from '@kk/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useClients, useMembership, useSession } from '@/auth/SessionProvider';
import { deck } from '@/copy/deck';
import { placeholders } from '@/copy/placeholders';
import type {
  ContentTeamRole,
  AdminAdventureInput,
  FamilyInviteInput,
  AdminMissionInput,
  QuestContentStatus,
  OrgPatch,
  AssignmentInput,
  AssignmentPatch,
  DenyCompletionInput,
  GroupInput,
  JoinCodeInput,
  StaffInviteInput,
  ClubBillingTermsInput,
} from '@/types/wire';

/** Query keys shared by every screen: ['org', orgId, …]. */
export const orgKeys = {
  org: (orgId: string) => ['org', orgId] as const,
  approvals: (orgId: string) => ['org', orgId, 'approvals'] as const,
  roster: (orgId: string) => ['org', orgId, 'roster'] as const,
  redemptions: (orgId: string) => ['org', orgId, 'redemptions'] as const,
  lookup: (orgId: string, code: string) => ['org', orgId, 'redemptions', 'lookup', code] as const,
  assignments: (orgId: string) => ['org', orgId, 'assignments'] as const,
  assignment: (orgId: string, id: string) => ['org', orgId, 'assignments', id] as const,
  groups: (orgId: string) => ['org', orgId, 'groups'] as const,
  staff: (orgId: string) => ['org', orgId, 'staff'] as const,
  familyInvites: (orgId: string) => ['org', orgId, 'family-invites'] as const,
  billing: (orgId: string) => ['org', orgId, 'billing'] as const,
};

/**
 * O06's keys carry no orgId, because the content carries no organization. Keeping them out of `orgKeys` is
 * not tidiness: `useInvalidate().org()` must never reach quest content, and an org switch must never refetch
 * it — the two things are scoped differently and the cache should say so.
 */
export const questKeys = {
  /** Every quest query sits under this, so a content write refreshes history and the audit log too. */
  all: () => ['admin', 'quest'] as const,
  content: () => ['admin', 'quest', 'adventures'] as const,
  versions: (id: string) => ['admin', 'quest', 'versions', id] as const,
  adventureVersions: (id: string) => ['admin', 'quest', 'adventure-versions', id] as const,
  team: () => ['admin', 'quest', 'team'] as const,
  icons: () => ['admin', 'quest', 'icons'] as const,
  audit: (id: string) => ['admin', 'quest', 'audit', id] as const,
};

function useOrgId(): string {
  const { orgId } = useSession();
  if (!orgId) throw new Error('no organization in session');
  return orgId;
}

export function useOrg() {
  const { data } = useClients();
  const orgId = useOrgId();
  return useQuery({ queryKey: orgKeys.org(orgId), queryFn: () => data.org(orgId) });
}

export function useApprovals() {
  const { data } = useClients();
  const orgId = useOrgId();
  return useQuery({ queryKey: orgKeys.approvals(orgId), queryFn: () => data.approvals(orgId) });
}

export function useRoster() {
  const { data } = useClients();
  const orgId = useOrgId();
  return useQuery({ queryKey: orgKeys.roster(orgId), queryFn: () => data.roster(orgId) });
}

export function useRedemptions() {
  const { data } = useClients();
  const orgId = useOrgId();
  return useQuery({
    queryKey: orgKeys.redemptions(orgId),
    queryFn: () => data.redemptions(orgId),
  });
}

/** O04 "Look up": runs only once a code is set; NOT_FOUND and the other codes surface through `error`. */
export function useLookup(code: string | null) {
  const { data } = useClients();
  const orgId = useOrgId();
  return useQuery({
    queryKey: orgKeys.lookup(orgId, code ?? ''),
    queryFn: () => data.lookup(orgId, code ?? ''),
    enabled: code !== null,
    staleTime: 0,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  const orgId = useOrgId();
  return {
    // ['org', orgId] is a prefix of every other key: exact, so the summary alone refetches.
    org: () => qc.invalidateQueries({ queryKey: orgKeys.org(orgId), exact: true }),
    approvals: () => qc.invalidateQueries({ queryKey: orgKeys.approvals(orgId) }),
    roster: () => qc.invalidateQueries({ queryKey: orgKeys.roster(orgId) }),
    redemptions: () => qc.invalidateQueries({ queryKey: orgKeys.redemptions(orgId) }),
    assignments: () => qc.invalidateQueries({ queryKey: orgKeys.assignments(orgId) }),
    // The list key prefixes every ['org', orgId, 'assignments', id]: exact when the detail was just primed.
    assignmentsList: () =>
      qc.invalidateQueries({ queryKey: orgKeys.assignments(orgId), exact: true }),
    groups: () => qc.invalidateQueries({ queryKey: orgKeys.groups(orgId) }),
    staff: () => qc.invalidateQueries({ queryKey: orgKeys.staff(orgId) }),
  };
}

export function useApprove() {
  const { data } = useClients();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (completionId: string) => data.approve(orgId, completionId),
    onSettled: () => Promise.all([invalidate.approvals(), invalidate.roster()]),
  });
}

export function useDeny() {
  const { data } = useClients();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { completionId: string; input: DenyCompletionInput }) =>
      data.deny(orgId, v.completionId, v.input),
    onSettled: () => Promise.all([invalidate.approvals(), invalidate.roster()]),
  });
}

export function useFulfill() {
  const { data } = useClients();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => data.fulfill(orgId, code),
    onSettled: (_r, _e, code) =>
      Promise.all([
        invalidate.redemptions(),
        qc.invalidateQueries({ queryKey: orgKeys.lookup(orgId, code) }),
      ]),
  });
}

// O03
export function useAssignments() {
  const { data } = useClients();
  const orgId = useOrgId();
  return useQuery({
    queryKey: orgKeys.assignments(orgId),
    queryFn: () => data.assignments(orgId),
  });
}

export function useAssignment(id: string | null) {
  const { data } = useClients();
  const orgId = useOrgId();
  return useQuery({
    queryKey: orgKeys.assignment(orgId, id ?? ''),
    queryFn: () => data.assignment(orgId, id ?? ''),
    enabled: id !== null,
  });
}

/** Create and update prime the per-assignment cache so the builder's URL change (/new → /:id) refetches nothing. */
export function useCreateAssignment() {
  const { data } = useClients();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignmentInput) => data.createAssignment(orgId, input),
    onSuccess: (a) => qc.setQueryData(orgKeys.assignment(orgId, a.id), a),
    onSettled: () => invalidate.assignmentsList(),
  });
}

export function useUpdateAssignment() {
  const { data } = useClients();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; patch: AssignmentPatch }) =>
      data.updateAssignment(orgId, v.id, v.patch),
    onSuccess: (a) => qc.setQueryData(orgKeys.assignment(orgId, a.id), a),
    onSettled: () => invalidate.assignmentsList(),
  });
}

/** Publishing writes one org task per task per targeted child: the roster's MY TASKS DONE totals change too. */
export function usePublishAssignment() {
  const { data } = useClients();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => data.publishAssignment(orgId, id),
    onSettled: () => Promise.all([invalidate.assignments(), invalidate.roster()]),
  });
}

// O05 — org admins only; a group admin's query fails with FORBIDDEN (the screen never issues it).
export function useGroups(enabled = true) {
  const { data } = useClients();
  const orgId = useOrgId();
  return useQuery({ queryKey: orgKeys.groups(orgId), queryFn: () => data.groups(orgId), enabled });
}

export function useCreateGroup() {
  const { data } = useClients();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: GroupInput) => data.createGroup(orgId, input),
    onSettled: () => Promise.all([invalidate.groups(), invalidate.org()]),
  });
}

export function useGenerateCode() {
  const { data } = useClients();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { groupId: string; input?: JoinCodeInput }) =>
      data.generateCode(orgId, v.groupId, v.input ?? {}),
    onSettled: () => Promise.all([invalidate.groups(), invalidate.org()]),
  });
}

export function useRevokeCode() {
  const { data } = useClients();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (groupId: string) => data.revokeCode(orgId, groupId),
    onSettled: () => Promise.all([invalidate.groups(), invalidate.org()]),
  });
}

/** O08's club header: the three names, and the badge letters the disc draws live beside them. */
export function useUpdateOrg() {
  const { data } = useClients();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (patch: OrgPatch) => data.updateOrg(orgId, patch),
    // the org is in the shell header too, so both it and the groups list read the new name
    onSettled: () => Promise.all([invalidate.org(), invalidate.groups()]),
  });
}

/** O08's staff list. The 409 for the last org admin is surfaced, never pre-empted by hiding the row. */
export function useRemoveStaff() {
  const { data } = useClients();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => data.removeStaff(orgId, id),
    onSettled: () => invalidate.staff(),
  });
}

export function useStaff(enabled = true) {
  const { data } = useClients();
  const orgId = useOrgId();
  return useQuery({ queryKey: orgKeys.staff(orgId), queryFn: () => data.staff(orgId), enabled });
}

export function useInviteStaff() {
  const { data } = useClients();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: StaffInviteInput) => data.inviteStaff(orgId, input),
    onSettled: () => invalidate.staff(),
  });
}

export function useRevokeInvite() {
  const { data } = useClients();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (inviteId: string) => data.revokeInvite(orgId, inviteId),
    onSettled: () => invalidate.staff(),
  });
}

/** The DataClient code, for deciding what happened — never for showing to anybody. */
export function errorCode(e: unknown): string {
  return isDataClientError(e) ? e.code : 'SERVER';
}

/**
 * What goes on screen when something fails. **Never the code** (Evan, 2026-09-16: *"Never render a raw
 * enum. FORBIDDEN on screen is the worst string in the product."*) and **never two codes joined by a
 * dot**, which is what approve-all used to do — the symptom of treating codes as copy.
 *
 * A hook rather than a function because one of the sentences needs the club's name: telling a group
 * admin to "ask an org admin at Boys & Girls Club of Decatur" is the difference between a dead end
 * and a next step.
 */
export function useErrorMessage(): (e: unknown) => string {
  const say = useMessageForCode();
  return (e: unknown) => {
    // two refusals of our own sign-in say what to do rather than naming the code (2026-09-26)
    const reason =
      isDataClientError(e) && typeof e.details === 'object' && e.details !== null
        ? (e.details as { reason?: unknown }).reason
        : undefined;
    if (reason === 'signInAgain') return deck.signIn.signInAgainToPublish;
    if (reason === 'notCompanyAccount') return deck.signIn.notCompanyAccount;
    if (reason === 'companyOnlyRole') return deck.o17.companyOnly;
    if (reason === 'secondPublisher') return deck.signIn.secondPublisher;
    // club billing (DECISIONS 92): a code or invite refused until the club pays says what to do, not "something went wrong"
    if (reason === 'billingRequired') return placeholders.billing.setUp;
    // working together: who has the pen, or the draft
    const name =
      isDataClientError(e) && typeof e.details === 'object' && e.details !== null
        ? String((e.details as { name?: unknown }).name ?? '')
        : '';
    if (reason === 'lockedBy' && name) return deck.o11.editing(name);
    if (reason === 'claimed' && name) return deck.o11.claimedBy(name);
    return say(errorCode(e));
  };
}

/** The same mapping for a code that arrives as DATA rather than as a throw (approve-all's `skipped`). */
export function useMessageForCode(): (code: string) => string {
  const orgName = useMembership()?.orgName ?? '';
  return (code: string) => {
    if (code === 'FORBIDDEN') return deck.errors.forbidden(orgName);
    const said = Object.entries(deck.errors).find(
      ([key, value]) => key === code && typeof value === 'string',
    );
    return (said?.[1] as string | undefined) ?? deck.errors.other;
  };
}

// ---- O06 · Quest Jr. content (super admin) -------------------------------------------------------------

/**
 * Every adventure with its missions. `enabled` is the caller's `superAdmin` read — **never a probe**: asking
 * and treating FORBIDDEN as "not an admin" is the inference M-41 deleted, and it would put a red error in a
 * club director's console on every page load.
 */
export function useQuestContent(enabled = true) {
  const { data } = useClients();
  return useQuery({ queryKey: questKeys.content(), queryFn: () => data.questContent(), enabled });
}

/** Every O06 write answers with the row it changed, and every one of them invalidates the single list. */
function useQuestMutation<TInput, TResult>(fn: (input: TInput) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => qc.invalidateQueries({ queryKey: questKeys.all() }),
  });
}

/** O14 · the icon library. Under `questKeys.all`, so a publish, retire or upload refreshes it with everything else. */
export function useIconLibrary() {
  const { data } = useClients();
  return useQuery({ queryKey: questKeys.icons(), queryFn: () => data.iconLibrary() });
}

/** The validator on a chosen file: a mutation, because it answers for one file and saves nothing. */
export function useCheckIcon() {
  const { data } = useClients();
  return useMutation({ mutationFn: (svg: string) => data.checkIcon(svg) });
}

export function useUploadIcon() {
  const { data } = useClients();
  return useQuestMutation((input: { key: string; svg: string }) =>
    data.uploadIcon(input.key, input.svg),
  );
}

export function usePublishIcon() {
  const { data } = useClients();
  return useQuestMutation((key: string) => data.publishIcon(key));
}

export function useRetireIcon() {
  const { data } = useClients();
  return useQuestMutation((key: string) => data.retireIcon(key));
}

export function useCreateAdventure() {
  const { data } = useClients();
  return useQuestMutation((input: AdminAdventureInput) => data.createAdventure(input));
}

export function useUpdateAdventure() {
  const { data } = useClients();
  return useQuestMutation((input: { id: string } & AdminAdventureInput) => {
    const { id, ...patch } = input;
    return data.updateAdventure(id, patch);
  });
}

export function useCreateMission() {
  const { data } = useClients();
  return useQuestMutation((input: AdminMissionInput & { adventureId: string }) =>
    data.createMission(input),
  );
}

export function useUpdateMission() {
  const { data } = useClients();
  return useQuestMutation((input: { id: string } & AdminMissionInput) => {
    const { id, ...patch } = input;
    return data.updateMission(id, patch);
  });
}

export function useSetMissionStatus() {
  const { data } = useClients();
  return useQuestMutation((input: { id: string; status: QuestContentStatus }) =>
    data.setMissionStatus(input.id, input.status),
  );
}

/** The editors: an adventure's missions in a new order. */
export function useReorderMissions() {
  const { data } = useClients();
  return useQuestMutation((input: { adventureId: string; missionIds: string[] }) =>
    data.reorderMissions(input.adventureId, input.missionIds),
  );
}

/** The editors: a mission that was never published, deleted. */
export function useDeleteMission() {
  const { data } = useClients();
  return useQuestMutation((id: string) => data.deleteMission(id));
}

/** The Team page: the content team, and who the caller is on it. */
export function useContentTeam(enabled = true) {
  const { data } = useClients();
  return useQuery({ queryKey: questKeys.team(), queryFn: () => data.contentTeam(), enabled });
}

export function useAddTeamMember() {
  const { data } = useClients();
  return useQuestMutation((input: { email: string; role: ContentTeamRole; endsOn?: string }) =>
    data.addTeamMember(input),
  );
}

export function useSetTeamRole() {
  const { data } = useClients();
  return useQuestMutation(
    ({ id, ...change }: { id: string; role?: ContentTeamRole; endsOn?: string | null }) =>
      data.setTeamRole(id, change),
  );
}

export function useRemoveTeamMember() {
  const { data } = useClients();
  return useQuestMutation((id: string) => data.removeTeamMember(id));
}

/** O11 "Discard draft". */
// ---- working together (2026-09-26) --------------------------------------------------------------------------------

export function useComments(missionId: string) {
  const { data } = useClients();
  return useQuery({
    queryKey: [...questKeys.all(), 'comments', missionId],
    queryFn: () => data.comments(missionId),
  });
}

export function useAddComment(missionId: string) {
  const { data } = useClients();
  return useQuestMutation((input: { anchor: string; text: string }) =>
    data.addComment(missionId, input),
  );
}

export function useResolveComment() {
  const { data } = useClients();
  return useQuestMutation((id: string) => data.resolveComment(id));
}

/** Takes or keeps the lock. Not a quest mutation: holding a lock changes nothing on screen until someone else looks. */
export function useLockMission() {
  const { data } = useClients();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; takeOver?: boolean }) =>
      data.lockMission(input.id, input.takeOver),
    onSuccess: (_l, input) => {
      if (input.takeOver) void qc.invalidateQueries({ queryKey: questKeys.all() });
    },
  });
}

export function useClaimReview() {
  const { data } = useClients();
  return useQuestMutation((id: string) => data.claimReview(id));
}

export function useDiscardMissionDraft() {
  const { data } = useClients();
  return useQuestMutation((id: string) => data.discardMissionDraft(id));
}

/** O13 "Copy into the draft". */
export function useCopyVersionToDraft() {
  const { data } = useClients();
  return useQuestMutation((input: { id: string; version: number }) =>
    data.copyVersionToDraft(input.id, input.version),
  );
}

/** An adventure's draft, published as its next version. */
export function usePublishAdventure() {
  const { data } = useClients();
  return useQuestMutation((id: string) => data.publishAdventure(id));
}

export function useDiscardAdventureDraft() {
  const { data } = useClients();
  return useQuestMutation((id: string) => data.discardAdventureDraft(id));
}

export function useAdventureVersions(id: string | null) {
  const { data } = useClients();
  return useQuery({
    queryKey: questKeys.adventureVersions(id ?? ''),
    queryFn: () => data.adventureVersions(id!),
    enabled: !!id,
  });
}

export function useRestoreAdventureVersion() {
  const { data } = useClients();
  return useQuestMutation((input: { id: string; version: number }) =>
    data.restoreAdventureVersion(input.id, input.version),
  );
}

/** Rereads the source lessons and flags what changed. Nothing republishes on its own. */
export function useSyncQuestSource() {
  const { data } = useClients();
  return useQuestMutation(() => data.syncQuestSource());
}

// ---- O09 · invite families ------------------------------------------------------------------------

export function useFamilyInvites(enabled = true) {
  const { data } = useClients();
  const orgId = useOrgId();
  return useQuery({
    queryKey: orgKeys.familyInvites(orgId),
    queryFn: () => data.familyInvites(orgId),
    enabled,
  });
}

/**
 * Sending and resending both invalidate the same list, and BOTH invalidate the roster: a family that joins
 * turns an invitation row into a roster row, and a screen showing one without the other is two truths.
 */
function useInviteMutation<TInput, TResult>(fn: (input: TInput) => Promise<TResult>) {
  const qc = useQueryClient();
  const orgId = useOrgId();
  return useMutation({
    mutationFn: fn,
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: orgKeys.familyInvites(orgId) }),
        qc.invalidateQueries({ queryKey: orgKeys.roster(orgId) }),
      ]),
  });
}

export function useInviteFamilies() {
  const { data } = useClients();
  const orgId = useOrgId();
  return useInviteMutation((input: FamilyInviteInput) => data.inviteFamilies(orgId, input));
}

export function useResendFamilyInvite() {
  const { data } = useClients();
  const orgId = useOrgId();
  return useInviteMutation((id: string) => data.resendFamilyInvite(orgId, id));
}

/** O13 · a mission's versions (CONTENT_ENGINE.md §4). */
export function useMissionVersions(id: string | null) {
  const { data } = useClients();
  return useQuery({
    queryKey: questKeys.versions(id ?? ''),
    queryFn: () => data.missionVersions(id!),
    enabled: !!id,
  });
}

/** O13 · "AUDIT LOG · THIS MISSION". */
export function useQuestAudit(missionId: string | null) {
  const { data } = useClients();
  return useQuery({
    queryKey: questKeys.audit(missionId ?? ''),
    queryFn: () => data.questAudit(missionId!),
    enabled: !!missionId,
  });
}

/** O13 · Restore: a publish of an old version, as the next version. */
export function useRestoreVersion() {
  const { data } = useClients();
  return useQuestMutation((input: { id: string; version: number }) =>
    data.restoreVersion(input.id, input.version),
  );
}

// ---- O18, a club's application -----------------------------------------------------------------------------------------
const applicationKeys = { all: () => ['admin', 'club-applications'] as const };

/** The Applications page (owners only). */
export function useClubApplications(enabled = true) {
  const { data } = useClients();
  return useQuery({
    queryKey: applicationKeys.all(),
    queryFn: () => data.clubApplications(),
    enabled,
  });
}

function useApplicationMutation<T>(fn: (id: string) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => qc.invalidateQueries({ queryKey: applicationKeys.all() }),
  });
}

export function useApproveClubApplication() {
  const { data } = useClients();
  return useApplicationMutation((id) => data.approveClubApplication(id));
}

export function useDeclineClubApplication() {
  const { data } = useClients();
  return useApplicationMutation((id) => data.declineClubApplication(id));
}

/** clubs.kiddiekredit.com/apply: no session. */
export function useSubmitClubApplication() {
  const { data } = useClients();
  return useMutation({
    mutationFn: (input: ClubApplicationInput) => data.submitClubApplication(input),
  });
}

// ---- Village Square, the admin -----------------------------------------------------------------------------------------
const villageKeys = {
  items: () => ['admin', 'village', 'items'] as const,
  partners: () => ['admin', 'village', 'partners'] as const,
  picks: () => ['admin', 'village', 'picks'] as const,
};

/** P35d's weekly pick line-up (28 Sep). */
export function useVillageWeekPicks() {
  const { data } = useClients();
  return useQuery({ queryKey: villageKeys.picks(), queryFn: () => data.villageWeekPicks() });
}

export function useVillageItems() {
  const { data } = useClients();
  return useQuery({ queryKey: villageKeys.items(), queryFn: () => data.villageItems() });
}

export function useVillagePartners() {
  const { data } = useClients();
  return useQuery({ queryKey: villageKeys.partners(), queryFn: () => data.villagePartners() });
}

/** Any change to Village Square: both lists read again, so a partner's approval shows on its cards at once. */
export function useVillageMutation<I, T>(fn: (input: I) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'village'] }),
  });
}

/** The admin's metrics: owners only, so an editor's page never asks. */
export function useMetrics(enabled = true) {
  const { data } = useClients();
  return useQuery({ queryKey: ['admin', 'metrics'] as const, queryFn: () => data.metrics(), enabled });
}

/** Help's FAQ (3.0.10). */
const faqKeys = { all: () => ['admin', 'faq'] as const };
export function useFaq() {
  const { data } = useClients();
  return useQuery({ queryKey: faqKeys.all(), queryFn: () => data.faq() });
}
export function useFaqMutation<I, T>(fn: (input: I) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => qc.invalidateQueries({ queryKey: faqKeys.all() }),
  });
}

/**
 * Whether the new Kredit Score is open (`PublicConfig.scoreEngineV2`): the portal then names the lowest band
 * "Starting" (lib/band.ts). The same query the builder reads the categories switch from, so it is fetched once.
 */
export function useNewScore(): boolean {
  const { data: clients } = useClients();
  const config = useQuery({ queryKey: ['public', 'config'], queryFn: () => clients.publicConfig(), staleTime: 5 * 60_000 });
  return config.data?.scoreEngineV2 === 'open';
}

/** Whether kiddiekredit.com/guides is live (`PublicConfig.guidesPage`; DECISIONS 81): the portal then links the Club Guide. */
export function useGuidesPage(): boolean {
  const { data: clients } = useClients();
  const config = useQuery({ queryKey: ['public', 'config'], queryFn: () => clients.publicConfig(), staleTime: 5 * 60_000 });
  return config.data?.guidesPage === 'open';
}

/** Club billing (DECISIONS 90): what the Settings billing section reads. */
export function useBilling(enabled = true) {
  const { data } = useClients();
  const orgId = useOrgId();
  return useQuery({ queryKey: orgKeys.billing(orgId), queryFn: () => data.billing(orgId), enabled });
}

/** Stripe's checkout or billing page: the director leaves the portal for Stripe and comes back to Settings. */
export function useBillingRedirect() {
  const { data } = useClients();
  const orgId = useOrgId();
  return useMutation({
    mutationFn: async (to: 'monthly' | 'yearly' | 'manage') => {
      const { url } = to === 'manage' ? await data.billingPortal(orgId) : await data.billingCheckout(orgId, to);
      window.location.assign(url);
    },
  });
}

// ---- the owners' Club Billing page (DECISIONS 92) ---------------------------------------------------------------------
const clubBillingKeys = { report: () => ['admin', 'club-billing'] as const };

/** Every club's month, for owners. `enabled` is the caller's owner read, never a probe. */
export function useClubBillingReport(enabled = true) {
  const { data } = useClients();
  return useQuery({ queryKey: clubBillingKeys.report(), queryFn: () => data.listClubBilling(), enabled });
}

/** Comp a club, or set its sponsored children and sponsor; the report reads again afterwards. */
export function useSetClubBillingTerms() {
  const { data } = useClients();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, input }: { orgId: string; input: ClubBillingTermsInput }) =>
      data.setClubBillingTerms(orgId, input),
    onSettled: () => qc.invalidateQueries({ queryKey: clubBillingKeys.report() }),
  });
}
